import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CropperPosition, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-photo-crop',
    templateUrl: './photo-crop.component.html',
    styleUrls: ['./photo-crop.component.scss'],
    standalone: false
})
export class PhotoCropComponent implements OnInit {
  croppedImage: any = '';
  croppedImageFile: any = '';
  imageFile: any = '';
  imageURL: string = '';
  transform: ImageTransform = {};
  croppedImageSizes: {height?: number; width?: number} = {}; // Calculated
  oldCroppedImageSizes: {height?: number; width?: number} = {}; // For checking which value changed
  imgOnPageSizes: {height?: number; width?: number} = {};
  scale = 1;
  cropper: CropperPosition = {
    x1: 0,
    x2: 0,
    y1: 0,
    y2: 0,
  };
  cropperMinWidth = 0;
  cropperMinHeight = 0;
  cropperMaxWidth = 10000;
  cropperMaxHeight = 10000;
  imgMedia: any;
  sizeChange = new Subject<number>();
  errors: string[] = [];
  zoomInterval: any;
  errorDescriptions: {[key: string]: string} = {
    min_height: 'Height is less than the minimum allowed height.',
    min_width: 'Width is less than the minimum allowed width.',
    max_height: 'Height exceeds the maximum allowed height.',
    max_width: 'Width exceeds the maximum allowed width.',
  };
  aspectRatio;

  ratio_x;
  ratio_y;
  img_h_ratio;
  img_w_ratio;
  page_img_h_ratio;
  page_img_w_ratio;
  firstSetCrop = true;
  freeMaxCrop = false;
  pendingSave = false;

  @ViewChild(ImageCropperComponent, {static: false})
  imageCropper!: ImageCropperComponent;

  @ViewChild('cropperWrapper', {static: false})
  wrapper!: ElementRef<HTMLDivElement>;

  private interaction$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<PhotoCropComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.aspectRatio = this.parseAspectRatio(this.data.ratio) ?? this.aspectRatio;
      this.imageURL = this.data.imageURL ?? this.imageURL;
      this.imageFile = this.data.imageFile ?? this.imageFile;
      this.cropperMinWidth = this.data.cropperMinWidth ?? this.cropperMinWidth;
      this.cropperMinHeight = this.data.cropperMinHeight ?? this.cropperMinHeight;
      this.cropperMaxWidth = this.data.cropperMaxWidth ?? this.cropperMaxWidth;
      this.cropperMaxHeight = this.data.cropperMaxHeight ?? this.cropperMaxHeight;
      this.imgMedia = this.data.imgMedia ?? this.imgMedia;

      if (this.data.freeMaxCrop) {
        this.freeMaxCrop = this.data.freeMaxCrop;
      }

      if (this.imageURL && this.imageURL !== '') {
        this.getImageResolutionUrl(this.imageURL);
      } else {
        this.getImageResolution(this.imageFile);
      }
    }
    this.sizeChange.pipe(debounceTime(500)).subscribe(values => {
      this.sizeChanged(values);
    });

    this.interaction$.pipe(debounceTime(100)).subscribe(() => this.checkBoxSize());
  }

  onCropperInteraction() {
    this.interaction$.next();
  }

  private checkBoxSize() {
    const pos = this.imageCropper.cropper as CropperPosition;
    const ratioX = this.imgMedia.width / this.imgOnPageSizes.width;
    const ratioY = this.imgMedia.height / this.imgOnPageSizes.height;
    const width = Math.round((pos.x2 - pos.x1) * ratioX);
    const height = Math.round((pos.y2 - pos.y1) * ratioY);

    this.croppedImageSizes = {width, height};
    this.checkForErrors(height, width);
  }

  parseAspectRatio(ratio: string): number | null {
    if (ratio) {
      const [numerator, denominator] = ratio.split('/').map(Number);
      if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
        return numerator / denominator;
      }
    }
    return null;
  }

  getImageResolution(file) {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          this.imgMedia = {
            width: img.width,
            height: img.height,
          };
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  getImageResolutionUrl(url) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const resolution = {width: img.naturalWidth, height: img.naturalHeight};
      this.imgMedia = {
        width: resolution.width,
        height: resolution.height,
      };
    };
    img.src = url;
  }

  imageCropped(event: any) {
    this.croppedImageSizes = {height: event.height, width: event.width};
    this.oldCroppedImageSizes = {height: event.height, width: event.width};
    this.checkForErrors(event.height, event.width);
    this.croppedImage = event.base64;
    this.croppedImageFile = this.base64ToFile(event.base64, 'cropped-image.png');

    if (this.pendingSave) {
      this.pendingSave = false;
      this.saveCroppedImage();
    }
  }

  base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, {type: mime});
  }

  cropperReady(imgOnPageSizes) {
    // returns size of the image on the page (not real one)
    this.imgOnPageSizes = imgOnPageSizes;
  }

  startZoom(step: number): void {
    this.changeZoom(step);
    this.zoomInterval = setInterval(() => {
      this.changeZoom(step);
    }, 100);
  }

  stopZoom(): void {
    clearInterval(this.zoomInterval);
  }

  changeZoom(stepSize) {
    let newScale = this.scale + parseFloat(stepSize);
    if (newScale < 0) {
      newScale = 0;
    }
    this.scale = newScale;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  compareSizes(
    croppedImageSizes: {height?: number; width?: number},
    oldCroppedImageSizes: {height?: number; width?: number},
  ): 'height' | 'width' | false {
    if (croppedImageSizes.height !== oldCroppedImageSizes.height) {
      return 'height';
    }
    if (croppedImageSizes.width !== oldCroppedImageSizes.width) {
      return 'width';
    }
    return false;
  }

  onSizeChange(values) {
    this.sizeChange.next(values);
  }

  sizeChanged(croppedImageSizes) {
    croppedImageSizes.width = Math.abs(croppedImageSizes.width);
    croppedImageSizes.height = Math.abs(croppedImageSizes.height);
    const heightOrWidthChanged = this.compareSizes(croppedImageSizes, this.oldCroppedImageSizes);

    //  img on page size (view size) /img (real size)  ratio
    this.ratio_x = this.imgOnPageSizes.width / this.imgMedia.width;
    this.ratio_y = this.imgOnPageSizes.height / this.imgMedia.height;
    this.img_h_ratio = croppedImageSizes.height / this.imgMedia.height;
    this.img_w_ratio = croppedImageSizes.width / this.imgMedia.width;
    this.page_img_h_ratio = this.imgMedia.height / this.imgOnPageSizes.height;
    this.page_img_w_ratio = this.imgMedia.width / this.imgOnPageSizes.width;

    if (croppedImageSizes.width > this.imgMedia.width || croppedImageSizes.height > this.imgMedia.height) {
      //oversized img
      if (this.aspectRatio) {
        if (heightOrWidthChanged === 'height') {
          this.croppedImageSizes.height = this.imgMedia.height;
          this.croppedImageSizes.width = this.imgMedia.height * this.aspectRatio;
        } else {
          this.croppedImageSizes.width = this.imgMedia.width;
          this.croppedImageSizes.height = this.imgMedia.width / this.aspectRatio;
        }
        this.updateCroppedImageSizes({...this.croppedImageSizes}, true, heightOrWidthChanged);
      } else {
        this.croppedImageSizes.width = this.imgMedia.width;
        this.croppedImageSizes.height = this.imgMedia.height;
        this.oldCroppedImageSizes = {...this.croppedImageSizes};
      }
    } else {
      // not oversized img
      //block height/width max size
      if (this.img_h_ratio > 1) {
        this.img_h_ratio = 1;
        this.croppedImageSizes.height = this.imgMedia.height;
      }
      if (this.img_w_ratio > 1) {
        this.img_w_ratio = 1;
        this.croppedImageSizes.width = this.imgMedia.width;
      }

      if (this.aspectRatio) {
        this.updateCroppedImageSizes({...this.croppedImageSizes}, true, heightOrWidthChanged);
      } else {
        this.setCropper(
          this.img_w_ratio,
          this.img_h_ratio,
          this.ratio_x,
          this.ratio_y,
          this.page_img_w_ratio,
          this.page_img_h_ratio,
        );
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 500);
      }
      this.checkForErrors(this.croppedImageSizes.height, this.croppedImageSizes.width);
    }
  }

  setCropper(img_w_ratio, img_h_ratio, ratio_x, ratio_y, page_img_w_ratio, page_img_h_ratio) {
    //  sizes of crop (view)
    const calc_h = this.imgOnPageSizes.height * img_h_ratio;
    const calc_w = this.imgOnPageSizes.width * img_w_ratio;
    const additional_w_size = calc_w - (this.cropper.x2 - this.cropper.x1);
    const additional_h_size = calc_h - (this.cropper.y2 - this.cropper.y1);

    this.cropper.x1 = this.cropper.x1 - additional_w_size / 2;
    this.cropper.x2 = this.cropper.x2 + additional_w_size / 2;
    this.cropper.y1 = this.cropper.y1 - additional_h_size / 2;
    this.cropper.y2 = this.cropper.y2 + additional_h_size / 2;

    // calculate height/width if crop is oversized - out of the image, add extra size
    if (this.cropper.y2 > this.imgOnPageSizes.height) {
      this.cropper.y2 = this.imgOnPageSizes.height;
      this.setCropperAxis('y', ratio_y, false, page_img_h_ratio);
    }

    if (this.cropper.y1 < 0) {
      this.cropper.y1 = 0;
      this.setCropperAxis('y', ratio_y, true, page_img_h_ratio);
    }

    if (this.cropper.x2 > this.imgOnPageSizes.width) {
      this.cropper.x2 = this.imgOnPageSizes.width;
      this.setCropperAxis('x', ratio_x, false, page_img_w_ratio);
    }

    if (this.cropper.x1 < 0) {
      this.cropper.x1 = 0;
      this.setCropperAxis('x', ratio_x, true, page_img_w_ratio);
    }
  }

  updateCroppedImageSizes(event, setCropper?: boolean, HorW?: string | boolean): void {
    let expectedHeight;
    let newWidth;

    if (HorW === 'width') {
      expectedHeight = event.width / this.aspectRatio;
      expectedHeight = Math.round(expectedHeight);
      newWidth = Math.round(expectedHeight * this.aspectRatio);
    } else if (HorW === 'height') {
      newWidth = event.height * this.aspectRatio;
      newWidth = Math.round(newWidth);
      expectedHeight = Math.round(newWidth / this.aspectRatio);
    }

    if (expectedHeight >= this.imgMedia.height) {
      expectedHeight = this.imgMedia.height;
      newWidth = expectedHeight * this.aspectRatio;
    }

    if (newWidth >= this.imgMedia.width) {
      newWidth = this.imgMedia.width;
      expectedHeight = newWidth / this.aspectRatio;
    }

    let img_h_ratio = expectedHeight / this.imgMedia.height;
    let img_w_ratio = newWidth / this.imgMedia.width;

    setTimeout(() => {
      this.croppedImageSizes.height = expectedHeight;
      this.croppedImageSizes.width = newWidth;
      this.croppedImageSizes = {...this.croppedImageSizes};
      this.oldCroppedImageSizes = {...this.croppedImageSizes};
      this.cdr.detectChanges();

      if (setCropper) {
        this.setCropper(
          img_w_ratio,
          img_h_ratio,
          this.ratio_x,
          this.ratio_y,
          this.page_img_w_ratio,
          this.page_img_h_ratio,
        );
      }

      setTimeout(() => {
        this.cdr.detectChanges();
      }, 1000);
    });
  }

  setCropperAxis(axis, ratio, belowZero, page_img_ratio) {
    const axis1 = `${axis}1`;
    const axis2 = `${axis}2`;
    const hOrW = axis === 'x' ? 'width' : 'height';
    const desired_crop_size_y = this.croppedImageSizes[hOrW] * ratio;
    const selection = this['cropper'][axis2] - this['cropper'][axis1];
    const notAddedSize = Math.abs(desired_crop_size_y - selection);
    const restOfPageSize = Math.round(
      Math.abs(this['imgOnPageSizes'][hOrW] - this['cropper'][axis2] - this['cropper'][axis1]),
    );

    if (restOfPageSize > notAddedSize) {
      if (belowZero) {
        this['cropper'][axis2] = this['cropper'][axis2] + notAddedSize;
      } else {
        this['cropper'][axis1] = this['cropper'][axis1] - notAddedSize;
      }
    } else {
      if (belowZero) {
        this['cropper'][axis2] = this['imgOnPageSizes'][hOrW];
      } else {
        this['cropper'][axis1] = 0;
      }
    }
  }

  private checkForErrors(height, width) {
    if (height < this.cropperMinHeight) {
      this.addError('min_height');
    } else {
      this.removeError('min_height');
    }

    if (width < this.cropperMinWidth) {
      this.addError('min_width');
    } else {
      this.removeError('min_width');
    }

    if (height > this.cropperMaxHeight) {
      this.addError('max_height');
    } else {
      this.removeError('max_height');
    }

    if (width > this.cropperMaxWidth) {
      this.addError('max_width');
    } else {
      this.removeError('max_width');
    }
  }

  private addError(name) {
    if (!this.errors.includes(name)) {
      this.errors.push(name);
    }
  }

  private removeError(name) {
    const indexToRemove = this.errors.indexOf(name);

    if (indexToRemove !== -1) {
      this.errors.splice(indexToRemove, 1);
    }
  }

  onSaveClick(): void {
    this.pendingSave = true;
    this.imageCropper.crop();
  }

  saveCroppedImage() {
    const ratioX = this.imgMedia.width / this.imgOnPageSizes.width;
    const ratioY = this.imgMedia.height / this.imgOnPageSizes.height;

    // Sizes of the uploaded img
    const realSizeCropper: CropperPosition = {
      x1: Math.round(this.cropper.x1 * ratioX),
      x2: Math.round(this.cropper.x2 * ratioX),
      y1: Math.round(this.cropper.y1 * ratioY),
      y2: Math.round(this.cropper.y2 * ratioY),
    };

    this.dialogRef.close({
      saved: true,
      originalImageFile: this.imageFile,
      croppedImageFile: this.croppedImageFile,
      croppedImage: this.croppedImage,
      croppedImageResolution: this.croppedImageSizes,
      cropper: realSizeCropper,
    });
  }

  closeDialog() {
    this.dialogRef.close(false);
  }
}

export interface CroppedImageData {
  saved?: boolean;
  originalImageFile: File;
  croppedImageFile: File;
  croppedImage: string;
  croppedImageResolution: {width: number; height: number};
  cropper: CropperPosition;
  isVideo?: boolean;
}
